import type { ExpressionBuilder, QueryRowType, Transaction } from "@rocicorp/zero";
import type { UserActor } from "@corpuscomplete/core/actor";
import { z } from "zod/v4";

import { AccessGrant } from "../../auth/grant";
import { Group } from "../../auth/group";
import { Scope } from "../../auth/scope";
import { ScopePolicy } from "../../auth/scope/policy";
import { Calendar } from "../../common/calendar";
import { temporalPlainDateSchema, timeZoneSchema } from "../../common/date-time/zod";
import { acquireAdvisoryLockIfServer } from "../../drizzle/advisory-lock";
import { createInsertSchema } from "../../utils/sql";
import { defineQuery } from "../../zero/transformer";
import { withZeroTransaction } from "../../zero/utils";
import { zql, type Schema } from "../../zero/schema";
import { FoodCollection } from "../food-collection";
import { MealSchedule } from "../meal-schedule";
import { PlannedMeal } from "../planned-meal";
import { mealPlanningSession } from "./meal-planning-session.sql";
import { MealPlanningSessionScopePolicy } from "./scope-policy";
import {
  mealPlanningSessionMemberContextDataSchema,
  mealPlanningSessionScheduleConfigDataSchema,
} from "./types";
import { Nutrient } from "../nutrient";

export namespace MealPlanningSession {
  const contentsViewCriteria = AccessGrant.criteria("meal_planning_session", "contents", [
    "editor",
    "viewer",
  ]);

  const listCursorSchema = z.object({ dateStart: temporalPlainDateSchema, id: z.uuid() });

  const createSchema = createInsertSchema(mealPlanningSession)
    .extend({
      defaultMealScheduleId: z.uuid().optional(),
      dateStart: temporalPlainDateSchema,
      dateEnd: temporalPlainDateSchema,
      timeZone: timeZoneSchema,
      foodCollectionId: z.uuid(),
      memberUserIds: z.array(z.uuid()).min(1),
    })
    .refine(({ dateStart, dateEnd }) => Temporal.PlainDate.compare(dateStart, dateEnd) <= 0, {
      message: "End date must be same as or after start date",
      path: ["dateEnd"],
    })
    .refine(({ memberUserIds }) => new Set(memberUserIds).size === memberUserIds.length, {
      message: "Session members must be unique",
      path: ["memberUserIds"],
    });

  const createPlannedMealDraftsSchema = PlannedMeal.createDrafts.schema
    .omit({ scopeId: true })
    .extend({ mealPlanningSessionId: z.uuid() });

  const visible = (actor: UserActor) => (eb: ExpressionBuilder<"mealPlanningSession", Schema>) =>
    eb.or(
      eb.exists(
        "scope",
        (scope) =>
          scope.where((scopeEb) => ScopePolicy.editable(scopeEb, actor, ["user", "space"])),
        { flip: true },
      ),
      eb.exists("grants", (grant) => AccessGrant.matching(grant, actor, contentsViewCriteria), {
        flip: true,
      }),
    );

  export const scheduleConfigDataSchema = mealPlanningSessionScheduleConfigDataSchema.describe(
    "Meal planning session schedule config data",
  );
  export const memberContextDataSchema = mealPlanningSessionMemberContextDataSchema.describe(
    "Meal planning session member context data",
  );
  export type ScheduleConfigData = z.infer<typeof scheduleConfigDataSchema>;
  export type ScheduleAssignment = ScheduleConfigData["assignments"][number];
  export type MemberContextData = z.infer<typeof memberContextDataSchema>;
  export type SlotTarget = MemberContextData["slotTargets"][number];
  export type Phase = "setup" | "intake" | "readyToPlan" | "planning";

  function infoShape() {
    return zql.mealPlanningSession
      .related("scope")
      .related("group", (g) => g.related("members", (member) => member.related("profile")))
      .related("plannedMealLinks", (link) =>
        link
          .related("plannedMeal", (meal) =>
            meal
              .related("plates", (plate) =>
                plate
                  .related("items", (item) =>
                    item
                      .related("serving", (serving) =>
                        serving.related("food", (food) => food.related("meta")),
                      )
                      .orderBy("sortIndex", "asc"),
                  )
                  .related("itemDescriptors", (descriptor) =>
                    descriptor.orderBy("sortIndex", "asc"),
                  ),
              )
              .related("events", (event) =>
                event
                  .related("assignments", (assignment) =>
                    assignment.related("foodLogSourceGroup").orderBy("userId", "asc"),
                  )
                  .orderBy("timeAt", "asc"),
              ),
          )
          .orderBy("timeCreated", "asc"),
      )
      .related("scheduleConfig")
      .related("memberContexts");
  }
  type RawInfo = QueryRowType<ReturnType<typeof infoShape>>;
  export type Info = Omit<RawInfo, "plannedMealLinks" | "scheduleConfig" | "memberContexts"> & {
    plannedMeals: PlannedMeal.Info[];
    scheduleConfig:
      | (Omit<NonNullable<RawInfo["scheduleConfig"]>, "data"> & {
          data: ScheduleConfigData;
        })
      | null;
    memberContexts: (Omit<RawInfo["memberContexts"][number], "data"> & {
      data: MemberContextData;
    })[];
  };
  export type ListCursor = z.infer<typeof listCursorSchema>;

  export const byId = defineQuery(z.object({ id: z.uuid() }), ({ ctx, args: { id } }) =>
    zql.mealPlanningSession
      .where("id", "=", id)
      .where(visible(ctx.actor))
      .related("scope")
      .related("group", (g) => g.related("members", (member) => member.related("profile")))
      .related("plannedMealLinks", (link) =>
        link
          .related("plannedMeal", (meal) =>
            meal
              .related("plates", (plate) =>
                plate
                  .related("items", (item) =>
                    item
                      .related("serving", (serving) =>
                        serving.related("food", (food) => food.related("meta")),
                      )
                      .orderBy("sortIndex", "asc"),
                  )
                  .related("itemDescriptors", (descriptor) =>
                    descriptor.orderBy("sortIndex", "asc"),
                  ),
              )
              .related("events", (event) =>
                event
                  .related("assignments", (assignment) =>
                    assignment.related("foodLogSourceGroup").orderBy("userId", "asc"),
                  )
                  .orderBy("timeAt", "asc"),
              ),
          )
          .orderBy("timeCreated", "asc"),
      )
      .related("scheduleConfig")
      .related("memberContexts")
      .one(),
  );

  export const list = defineQuery(
    z.object({
      scopeId: z.uuid(),
      limit: z.number().min(1).max(50),
      cursor: listCursorSchema.nullable(),
      dir: z.union([z.literal("forward"), z.literal("backward")]),
    }),
    ({ ctx, args }) => {
      const orderByDir = args.dir === "forward" ? "desc" : "asc";
      let query = zql.mealPlanningSession
        .where("scopeId", "=", args.scopeId)
        .where(visible(ctx.actor))
        .related("scope")
        .related("group", (g) => g.related("members", (member) => member.related("profile")))
        .orderBy("dateStart", orderByDir)
        .orderBy("id", orderByDir)
        .limit(args.limit);
      if (args.cursor) {
        query = query.start(args.cursor, { inclusive: false });
      }
      return query;
    },
  );
  export type Preview = QueryRowType<typeof list>;

  export type Create = z.infer<typeof createSchema>;

  export type CompleteViolationTarget =
    | { entity: "mealPlanningSession"; code: "noPlannedMeals" }
    | {
        entity: "plannedMeal";
        id: string;
        code: "emptyName" | "noPlates" | "noInstances" | "noEvents";
      }
    | { entity: "event"; mealId: string; id: string; code: "noAssignments" }
    | {
        entity: "plate";
        mealId: string;
        id: string;
        code: "noItems" | "hasDescriptors" | "noAssignments";
      };

  export type CompleteViolations = {
    mealPlanningSession: { noPlannedMeals?: true };
    plannedMeals: Record<string, PlannedMeal.PublishViolations>;
    firstTarget: CompleteViolationTarget;
  };

  export function asInfo(raw: RawInfo): Info {
    return {
      ...raw,
      plannedMeals: raw.plannedMealLinks.flatMap((link) =>
        link.plannedMeal ? [PlannedMeal.asInfo(link.plannedMeal)] : [],
      ),
      scheduleConfig: raw.scheduleConfig
        ? {
            ...raw.scheduleConfig,
            data: scheduleConfigDataSchema.parse(raw.scheduleConfig.data),
          }
        : null,
      memberContexts: raw.memberContexts.map((context) => ({
        ...context,
        data: memberContextDataSchema.parse(context.data),
      })),
    };
  }

  export function phase(args: {
    session: Pick<Info, "scheduleConfig" | "group" | "memberContexts">;
    plannerSessionExists: boolean;
  }): Phase {
    if (!args.session.scheduleConfig) return "setup";
    const memberIds = args.session.group?.members.map((member) => member.userId) ?? [];
    const contextUserIds = new Set(args.session.memberContexts.map((context) => context.userId));
    if (args.plannerSessionExists) return "planning";
    return memberIds.every((memberId) => contextUserIds.has(memberId)) ? "readyToPlan" : "intake";
  }

  export function assignmentForDate(
    config: ScheduleConfigData,
    date: Temporal.PlainDate,
  ): ScheduleAssignment {
    const dayOfWeek = Calendar.asDayOfWeek(date);
    const dayMatch = config.assignments.find(
      (assignment) =>
        assignment.dateSelector.type === "dayOfWeek" &&
        assignment.dateSelector.values.includes(dayOfWeek),
    );
    const defaultAssignment = config.assignments.find(
      (assignment) => assignment.dateSelector.type === "default",
    );
    if (!defaultAssignment) {
      throw new Error("Schedule config must contain a default assignment");
    }
    return dayMatch ?? defaultAssignment;
  }

  function slotTargetKey(
    target: Pick<SlotTarget, "mealScheduleId" | "mealScheduleSlotId" | "nutritionProgramDayId">,
  ) {
    return `${target.mealScheduleId}:${target.mealScheduleSlotId}:${target.nutritionProgramDayId ?? "null"}`;
  }

  function usedSlotKeys(
    session: Pick<Info, "dateStart" | "dateEnd" | "scheduleConfig">,
  ): Set<string> {
    if (!session.scheduleConfig) return new Set();
    const keys = new Set<string>();
    let current = session.dateStart;
    while (Temporal.PlainDate.compare(current, session.dateEnd) <= 0) {
      const assignment = assignmentForDate(session.scheduleConfig.data, current);
      for (const slot of assignment.slots) {
        keys.add(
          slotTargetKey({
            mealScheduleId: assignment.mealScheduleId,
            mealScheduleSlotId: slot.mealScheduleSlotId,
            nutritionProgramDayId: null,
          }),
        );
      }
      current = current.add({ days: 1 });
    }
    return keys;
  }

  export type SlotTargetResolution =
    | { status: "included"; nutrientTargets: Nutrient.Targets }
    | { status: "skipped" }
    | { status: "missing" };
  export type ResolveSlotTargetArgs = {
    slotTargets: readonly SlotTarget[];
    mealScheduleId: string;
    mealScheduleSlotId: string;
    nutritionProgramDayId: string | null;
  };

  export function resolveSlotTarget(args: ResolveSlotTargetArgs): SlotTargetResolution {
    const exactKey = slotTargetKey(args);
    const fallbackKey = slotTargetKey({ ...args, nutritionProgramDayId: null });
    const target =
      args.slotTargets.find((candidate) => slotTargetKey(candidate) === exactKey) ??
      args.slotTargets.find((candidate) => slotTargetKey(candidate) === fallbackKey);
    if (!target) return { status: "missing" };
    return target.status === "included"
      ? { status: "included", nutrientTargets: target.nutrientTargets }
      : { status: "skipped" };
  }

  function assertMemberContextCoversSession(
    session: Pick<Info, "dateStart" | "dateEnd" | "scheduleConfig">,
    data: MemberContextData,
  ): void {
    const usedKeys = usedSlotKeys(session);
    const actualKeys = new Set<string>();
    let hasIncludedUsedSlot = false;
    for (const target of data.slotTargets) {
      const key = slotTargetKey(target);
      if (actualKeys.has(key)) throw new Error("Slot target includes duplicate keys");
      actualKeys.add(key);
      const nullKey = slotTargetKey({ ...target, nutritionProgramDayId: null });
      if (!usedKeys.has(nullKey))
        throw new Error("Slot target includes a slot outside the session schedule");
      if (target.status === "included") hasIncludedUsedSlot = true;
    }
    for (const key of usedKeys) {
      if (!actualKeys.has(key))
        throw new Error("Member context must cover every used schedule slot");
    }
    if (!hasIncludedUsedSlot) throw new Error("At least one schedule slot must be included");
  }

  export async function memberContextExists(
    tx: Transaction,
    args: { mealPlanningSessionId: string; userId: string },
  ) {
    const row = await tx.run(
      zql.mealPlanningSessionMemberContext
        .where("mealPlanningSessionId", "=", args.mealPlanningSessionId)
        .where("userId", "=", args.userId)
        .one(),
    );
    return row !== undefined;
  }

  export async function memberContextsComplete(tx: Transaction, mealPlanningSessionId: string) {
    const session = await fetchById(tx, mealPlanningSessionId);
    const memberIds = session?.group?.members.map((member) => member.userId) ?? [];
    if (memberIds.length === 0) return false;
    const rows = await tx.run(
      zql.mealPlanningSessionMemberContext.where(
        "mealPlanningSessionId",
        "=",
        mealPlanningSessionId,
      ),
    );
    const completedMemberIds = new Set(rows.map((row) => row.userId));
    return memberIds.every((memberId) => completedMemberIds.has(memberId));
  }

  export async function upsertMemberContextForMember(
    tx: Transaction,
    args: {
      session: Pick<Info, "id" | "dateStart" | "dateEnd" | "scheduleConfig" | "group">;
      userId: string;
      data: MemberContextData;
    },
  ) {
    if (!Group.hasMember(args.session.group, args.userId)) {
      throw new Error("User is not a member of this meal planning session");
    }
    assertMemberContextCoversSession(args.session, args.data);
    const timeNow = Temporal.Now.instant();
    const existing = await tx.run(
      zql.mealPlanningSessionMemberContext
        .where("mealPlanningSessionId", "=", args.session.id)
        .where("userId", "=", args.userId)
        .one(),
    );
    if (existing) {
      await tx.mutate.mealPlanningSessionMemberContext.update({
        mealPlanningSessionId: args.session.id,
        userId: args.userId,
        data: args.data,
        timeUpdated: timeNow,
      });
    } else {
      await tx.mutate.mealPlanningSessionMemberContext.insert({
        mealPlanningSessionId: args.session.id,
        userId: args.userId,
        data: args.data,
        timeCreated: timeNow,
        timeUpdated: timeNow,
      });
    }
  }

  function copyDefaultAssignment(schedule: MealSchedule.Info): ScheduleAssignment {
    return {
      mealScheduleId: schedule.id,
      dateSelector: { type: "default" },
      name: schedule.name,
      slots: schedule.slots.map((slot) => ({
        mealScheduleSlotId: slot.id,
        name: slot.name,
        time: slot.time,
        sortIndex: slot.sortIndex,
      })),
    };
  }

  export function completeViolations(
    session: Pick<Info, "plannedMeals">,
  ): CompleteViolations | null {
    const violations: Omit<CompleteViolations, "firstTarget"> = {
      mealPlanningSession: {},
      plannedMeals: {},
    };
    let firstTarget: CompleteViolationTarget | undefined;
    const setFirstTarget = (target: CompleteViolationTarget) => {
      firstTarget ??= target;
    };

    if (session.plannedMeals.length === 0) {
      violations.mealPlanningSession.noPlannedMeals = true;
      setFirstTarget({ entity: "mealPlanningSession", code: "noPlannedMeals" });
    }

    for (const meal of session.plannedMeals) {
      const mealViolations = PlannedMeal.publishViolations(meal);
      if (!mealViolations) {
        continue;
      }
      violations.plannedMeals[meal.id] = mealViolations;
      const target = mealViolations.firstTarget;
      setFirstTarget(
        target.entity === "plate" || target.entity === "event"
          ? { ...target, mealId: meal.id }
          : { ...target, entity: "plannedMeal", id: meal.id },
      );
    }

    return firstTarget ? { ...violations, firstTarget } : null;
  }

  export async function fetchById(tx: Transaction, id: string): Promise<Info | null> {
    const session = await tx.run(infoShape().where("id", "=", id).one());
    return session ? asInfo(session) : null;
  }

  export async function requireById(tx: Transaction, id: string): Promise<Info> {
    const session = await fetchById(tx, id);
    if (!session) {
      throw new Error("Meal planning session not found");
    }
    return session;
  }

  export async function lock(tx: Transaction, id: string): Promise<void> {
    await acquireAdvisoryLockIfServer(tx, "mealPlanningSession", id, "exclusive");
  }

  async function requireWithParentWrite(tx: Transaction, actor: UserActor, id: string) {
    await lock(tx, id);
    const session = await requireById(tx, id);
    await ScopePolicy.requireWritable(tx, actor, session.scopeId, ["user", "space"]);
    return { session };
  }

  async function assertMemberEligibleForScope(
    tx: Transaction,
    scopeId: string,
    userId: string,
  ): Promise<void> {
    if (tx.location === "client") return;
    const scope = await Scope.byId(tx, scopeId, ["user", "space"]);
    if (scope.kind === "user") {
      if (userId !== scope.userId) {
        throw new Error("Personal meal planning sessions can only add the scope owner directly");
      }
      return;
    }

    if (!(await Group.isMember(tx, userId, scope.groupId))) {
      throw new Error("Session members must be members of the parent space");
    }
  }

  async function assertMembersEligibleForScope(
    tx: Transaction,
    scopeId: string,
    userIds: readonly string[],
  ): Promise<void> {
    if (tx.location === "client") return;
    const scope = await Scope.byId(tx, scopeId, ["user", "space"]);
    if (scope.kind === "user") {
      if (userIds.length !== 1 || userIds[0] !== scope.userId) {
        throw new Error("Personal meal planning sessions must include only the scope owner");
      }
      return;
    }

    const memberUserIds = new Set(await Group.listMemberUserIds(tx, scope.groupId));
    if (userIds.some((userId) => !memberUserIds.has(userId))) {
      throw new Error("Session members must be members of the parent space");
    }
  }

  async function assertMemberUnassigned(tx: Transaction, sessionId: string, userId: string) {
    const meals = await PlannedMeal.fetchByMealPlanningSessionId(tx, sessionId);
    if (
      meals.some((meal) => meal.status === "draft" && meal.plates.some((p) => p.userId === userId))
    ) {
      throw new Error("Cannot remove a member assigned to a session-origin draft meal plate");
    }
  }

  export const create = withZeroTransaction(createSchema, async ({ tx, ctx, args }) => {
    await ScopePolicy.requireWritable(tx, ctx.actor, args.scopeId, ["user", "space"]);
    await assertMembersEligibleForScope(tx, args.scopeId, args.memberUserIds);
    const defaultMealScheduleId =
      args.defaultMealScheduleId ?? (await MealSchedule.firstDefaultForScope(tx, args.scopeId)).id;
    const schedule = await MealSchedule.requireByScope(tx, args.scopeId, defaultMealScheduleId);
    const scheduleConfig = scheduleConfigDataSchema.parse({
      version: 1,
      assignments: [copyDefaultAssignment(schedule)],
    });

    const timeNow = Temporal.Now.instant();
    await Group.create(tx, { id: args.id, memberUserIds: args.memberUserIds });
    await tx.mutate.mealPlanningSession.insert({
      id: args.id,
      timeCreated: timeNow,
      timeUpdated: timeNow,
      scopeId: args.scopeId,
      dateStart: args.dateStart,
      dateEnd: args.dateEnd,
      timeZone: args.timeZone,
    });
    await tx.mutate.mealPlanningSessionScheduleConfig.insert({
      mealPlanningSessionId: args.id,
      timeCreated: timeNow,
      timeUpdated: timeNow,
      data: scheduleConfig,
    });
    await Scope.create(tx, { kind: "mealPlanningSession", id: args.id });
    await FoodCollection.createSessionWorkingCollection(tx, {
      id: args.foodCollectionId,
      scopeId: args.id,
    });
    await AccessGrant.add(
      tx,
      AccessGrant.assignment(args.id, "meal_planning_session", "contents", "editor"),
      { type: "group", id: args.id },
    );
    await AccessGrant.add(
      tx,
      AccessGrant.assignment(args.foodCollectionId, "food_collection", "contents", "editor"),
      { type: "group", id: args.id },
    );
  });

  export type CreatePlannedMealDrafts = z.infer<typeof createPlannedMealDrafts.schema>;
  export const createPlannedMealDrafts = withZeroTransaction(
    createPlannedMealDraftsSchema,
    async ({ tx, ctx, args }) => {
      await lock(tx, args.mealPlanningSessionId);
      const session = await requireById(tx, args.mealPlanningSessionId);
      if (!(await MealPlanningSessionScopePolicy.canEditContents(tx, ctx.actor, session.id))) {
        await ScopePolicy.requireWritable(tx, ctx.actor, session.scopeId, ["user", "space"]);
      }

      await PlannedMeal.createDraftsForAllowedPlateUsers(tx, ctx.actor, {
        scopeId: session.scopeId,
        meals: args.meals,
        allowedPlateUserIds: session.group?.members.map((member) => member.userId) ?? [],
      });
      await Promise.all(
        args.meals.map((meal) =>
          linkPlannedMealInternal(tx, {
            mealPlanningSessionId: session.id,
            scopeId: session.scopeId,
            plannedMealId: meal.id,
          }),
        ),
      );
    },
  );

  export const addMember = withZeroTransaction(
    z.object({ id: z.uuid(), userId: z.uuid() }),
    async ({ tx, ctx, args }) => {
      const { session } = await requireWithParentWrite(tx, ctx.actor, args.id);
      await assertMemberEligibleForScope(tx, session.scopeId, args.userId);
      if (await Group.isMember(tx, args.userId, session.id)) {
        return;
      }
      await Group.addMember(tx, { groupId: session.id, userId: args.userId });
    },
  );

  export const upsertMemberContext = withZeroTransaction(
    z.object({ mealPlanningSessionId: z.uuid(), userId: z.uuid(), data: memberContextDataSchema }),
    async ({ tx, ctx, args }) => {
      const { session } = await requireWithParentWrite(tx, ctx.actor, args.mealPlanningSessionId);
      await upsertMemberContextForMember(tx, {
        session,
        userId: args.userId,
        data: args.data,
      });
    },
  );

  export const removeMember = withZeroTransaction(
    z.object({ id: z.uuid(), userId: z.uuid() }),
    async ({ tx, ctx, args }) => {
      const { session } = await requireWithParentWrite(tx, ctx.actor, args.id);
      await assertMemberUnassigned(tx, session.id, args.userId);
      if (!(await Group.isMember(tx, args.userId, session.id))) {
        throw new Error("User is not a member of this meal planning session");
      }
      const memberCount = session.group?.members.length ?? 0;
      if (memberCount <= 1) {
        throw new Error("Meal planning session requires at least one member");
      }
      await Group.removeMember(tx, { groupId: session.id, userId: args.userId });
    },
  );

  export async function requireCanCreateInvite(
    tx: Transaction,
    actor: UserActor,
    groupId: string,
  ): Promise<void> {
    await requireWithParentWrite(tx, actor, groupId);
  }

  export async function requireCanRevokeInvite(
    tx: Transaction,
    actor: UserActor,
    args: { id: string; groupId: string },
  ): Promise<void> {
    await lock(tx, args.groupId);
    const session = await requireById(tx, args.groupId);
    await ScopePolicy.requireWritable(tx, actor, session.scopeId, ["user", "space"]);
    const invite = await Group.requireInviteById(tx, args.id);
    if (invite.groupId !== session.id) {
      throw new Error("Invite does not belong to this meal planning session");
    }
  }

  export async function acceptInvite(
    tx: Transaction,
    args: { token: string; userId: string; mealPlanningSessionId: string },
  ): Promise<{ groupId: string }> {
    await lock(tx, args.mealPlanningSessionId);
    const session = await requireById(tx, args.mealPlanningSessionId);
    return Group.acceptInvite(tx, {
      token: args.token,
      userId: args.userId,
      groupId: session.id,
    });
  }

  export async function linkPlannedMealInternal(
    tx: Transaction,
    args: { mealPlanningSessionId: string; scopeId: string; plannedMealId: string },
  ): Promise<void> {
    const timeNow = Temporal.Now.instant();
    await tx.mutate.mealPlanningSessionPlannedMeal.insert({
      mealPlanningSessionId: args.mealPlanningSessionId,
      scopeId: args.scopeId,
      plannedMealId: args.plannedMealId,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
  }
}
